import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { api } from '../../services/api';
import { uploadServiceMedia, deleteServiceMedia, listServiceMedia, validateFile } from '../../services/uploadService';
import { duplicateVariationError } from './variationValidation';
import { ServiceCategoryResponse, LookupEntryResponse } from '../../types';
import {
  CreateOfferingV2Request,
  SupportedCarPayload,
  VariationPayload,
  UpsertVariantRow,
  UpdateOfferingV2Request,
  ReplaceVariationsRequest,
  OfferingV2Response,
  VariantStatus,
  OfferingStatus,
  BulkUpsertCarVariantsRequest,
} from '../../types/offeringsV2';
import { useLanguage } from '../../i18n/LanguageContext';
import { isConcurrentUpdateError, CONCURRENT_UPDATE_MESSAGE } from '../../services/errors';
import { useOffersT } from '../offers/offersI18n';
import { belowGuard, priceGuardMessage, priceGuardOf, type PriceGuard } from './priceGuard';
import ServicePageSection from './ServicePageSection';
import ServicePagePreview from './ServicePagePreview';
import VariationTextsEditor from './VariationTextsEditor';
import ValueDescriptionEditor from './ValueDescriptionEditor';
import {
  allErrorsInline,
  clearErrorsFor,
  emptyServicePageDraft,
  fieldErrorsOf,
  hasStep1Error,
  remapVariationErrorKeys,
  servicePageCreateFields,
  servicePageDraftFrom,
  servicePageUpdateFields,
  validateServicePage,
  durationRange,
  type FieldErrors,
  type ServicePageDraft,
} from './servicePageDraft';

interface Props {
  token: string;
  onClose: () => void;
  onSuccess: () => void;
  /** When set, the wizard loads this offering and saves edits instead of creating. */
  editOfferingId?: string;
}

type Step = 1 | 2;

// A variation the car uses: which pool variation (by index) + which of its values apply to this car.
interface VariationRef {
  poolIdx: number;
  enabled: number[];
}

interface CarRow {
  localId: string;
  serverCarId?: string;          // present when the car already exists on the server (edit mode)
  carMakeLookupId: string;
  carModelLookupId: string;
  carType: string;
  carMakeName?: string;
  carModelName?: string;
  // Per-car variations (a subset of the offering-level pool). Empty = single price for this car.
  variationRefs: VariationRef[];
  // Inline-priced rows keyed by combo string (value indices in variationRefs order; "" = single price).
  // status + rejectionReason are populated only in EDIT mode from the server's VariantView.
  priceByCombo: Record<string, { price: string; durationOverrideMinutes: string; status?: VariantStatus; rejectionReason?: string | null }>;
  expanded: boolean;
  selected: boolean;
}

interface VariationDraft {
  id?: string;                   // server id (edit mode); absent = new dimension
  name: string;
  /** Not edited in the wizard; carried through so a save does not wipe it. */
  nameAr?: string;
  selectionMode?: 'SINGLE' | 'MULTI';
  // Spec 081a option texts (optional; '' when unset).
  description?: string;
  descriptionAr?: string;
  moreDetails?: string;
  moreDetailsAr?: string;
  values: { id?: string; label: string; labelAr?: string; description: string; descriptionAr?: string }[];
}

const trimOrUndefined = (s?: string) => ((s ?? '').trim() || undefined);

const comboKey = (indices: number[]) => indices.join('_');

// #908 item 4: a combination's duration override is stored in minutes (like the flat duration),
// but the grid took days only, so a garage typed hours as a fraction of a day (16.6694… days).
// Entered as days + hours; a zero total means "no override".
const splitOverride = (minutes: string): { days: string; hours: string } => {
  const m = Math.max(0, Math.round(parseFloat(minutes) || 0));
  if (m === 0) return { days: '', hours: '' };
  const days = Math.floor(m / 1440);
  const hours = Math.round(((m % 1440) / 60) * 100) / 100;
  return { days: days ? String(days) : '', hours: hours ? String(hours) : '' };
};
const joinOverride = (days: string, hours: string): string => {
  const m = Math.round((parseFloat(days) || 0) * 1440 + (parseFloat(hours) || 0) * 60);
  return m > 0 ? String(m) : '';
};

function DurationOverrideInput({ minutes, onChange }: { minutes: string; onChange: (minutes: string) => void }) {
  // Local text so partial input ("1.", "0") survives; re-sync only when the value changes from
  // outside (hydrate, a value removal shifting rows), never on our own edits.
  const [text, setText] = useState(() => splitOverride(minutes));
  useEffect(() => {
    if (joinOverride(text.days, text.hours) !== joinOverride('', String((parseFloat(minutes) || 0) / 60))) {
      setText(splitOverride(minutes));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minutes]);
  const update = (patch: Partial<{ days: string; hours: string }>) => {
    const next = { ...text, ...patch };
    setText(next);
    onChange(joinOverride(next.days, next.hours));
  };
  return (
    <div className="v2-duration-override">
      <input type="number" min="0" step="1" placeholder="(default)" aria-label="Duration override days"
        value={text.days} onChange={(e) => update({ days: e.target.value })} />
      <span>d</span>
      <input type="number" min="0" step="0.5" placeholder="0" aria-label="Duration override hours"
        value={text.hours} onChange={(e) => update({ hours: e.target.value })} />
      <span>h</span>
    </div>
  );
}

// Removing value `vvi` from the variation at combo position `pos`: drop the rows that priced it
// and shift higher value indices down, so every other row keeps its price and duration (#908).
const dropValueFromCombos = <T,>(byCombo: Record<string, T>, pos: number, vvi: number): Record<string, T> => {
  const out: Record<string, T> = {};
  for (const [key, cell] of Object.entries(byCombo)) {
    if (key === '') { out[key] = cell; continue; }
    const combo = key.split('_').map(Number);
    const val = combo[pos];
    if (val === vvi) continue;
    if (val > vvi) combo[pos] = val - 1;
    out[comboKey(combo)] = cell;
  }
  return out;
};

const cartesian = (ranges: number[][]): number[][] => {
  let acc: number[][] = [[]];
  for (const range of ranges) {
    const next: number[][] = [];
    for (const prefix of acc) {
      for (const v of range) next.push([...prefix, v]);
    }
    acc = next;
  }
  return acc;
};

const carCombos = (refs: VariationRef[]): number[][] =>
  refs.length === 0 ? [[]] : cartesian(refs.map((r) => r.enabled));

let _localIdCounter = 0;
const nextLocalId = () => `c${++_localIdCounter}`;

const MAX_VARIATIONS = 10;
// #414 — values per variation raised 8 → 50 (matches the backend DTO cap).
const MAX_VALUES_PER_VARIATION = 50;
const carTypeKey = (t: string) => (t && t.trim() ? t.trim() : '');
// A car needs saving only when its rows differ from what the server already holds.
// Without this, editing one field rewrote every car — which is how a thumbnail change
// turned into hundreds of requests, 20 minutes, and a half-applied save when the token expired.
const variantFingerprint = (r: { variationValueIds: string[]; price: number; durationOverrideMinutes?: number | null }) =>
  `${[...r.variationValueIds].sort().join(',')}|${Number(r.price)}|${r.durationOverrideMinutes ?? ''}`;

const sameVariantSet = (
  desired: { variationValueIds: string[]; price: number; durationOverrideMinutes?: number | null }[],
  current: { variationValueIds: string[]; price: number; durationOverrideMinutes: number | null }[],
) => {
  if (desired.length !== current.length) return false;
  const a = desired.map(variantFingerprint).sort();
  const b = current.map(variantFingerprint).sort();
  return a.every((v, i) => v === b[i]);
};

const carIdentityKey = (make: string, model: string, type: string) => `${make}|${model}|${carTypeKey(type)}`;

interface BodyProps extends Props {
  /** Ticket #996: throw away local state and load the service again from the server. */
  onReload: () => void;
}

function AddServiceModalV2Body({ token, onClose, onSuccess, editOfferingId, onReload }: BodyProps) {
  const { t: _t } = useLanguage();
  const { t: offersT, lang } = useOffersT();
  const isEdit = !!editOfferingId;
  // Token refresh hands us a new token string. Effects that hydrate the form must not
  // re-run on it, or they overwrite unsaved edits (#908 item 5); they read the latest
  // token through this ref instead.
  const tokenRef = useRef(token);
  tokenRef.current = token;


  const [step, setStep] = useState<Step>(1);
  const [loadingOffering, setLoadingOffering] = useState(isEdit);

  // --- Step 1: Basics ---
  const [categories, setCategories] = useState<ServiceCategoryResponse[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [subcategoryId, setSubcategoryId] = useState('');
  // Spec 081a: description, warranty and the new service-page texts live in one draft.
  const [page, setPage] = useState<ServicePageDraft>(emptyServicePageDraft);
  // Spec 081a: the loaded offering's durations, the fallbacks of the duration rule (design §2b).
  const [loadedDurations, setLoadedDurations] = useState<{ flat: number | null; legacy: number | null }>({ flat: null, legacy: null });
  const patchPage = (patch: Partial<ServicePageDraft>) => {
    setPage((prev) => ({ ...prev, ...patch }));
    setFieldErrors((prev) => clearErrorsFor(prev, Object.keys(patch)));
  };
  const [status, setStatus] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE');
  // #1363: the offering's real status in edit mode. Only a live (ACTIVE) or paused (INACTIVE)
  // service can be toggled; one awaiting approval or rejected keeps its status, so the
  // wizard must not send ACTIVE for it.
  const [serverStatus, setServerStatus] = useState<OfferingStatus | null>(null);
  const statusToggleable = !isEdit || serverStatus === 'ACTIVE' || serverStatus === 'INACTIVE';
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [existingThumbnailUrl, setExistingThumbnailUrl] = useState<string | null>(null);
  const [additionalPhotos, setAdditionalPhotos] = useState<File[]>([]);
  const [additionalPreviews, setAdditionalPreviews] = useState<string[]>([]);
  // Saved photos carry their mediaId so they can be deleted — `photoUrls` on the offering
  // response is bare strings, which is why they used to be un-removable.
  const [existingPhotos, setExistingPhotos] = useState<{ mediaId: string; downloadUrl: string }[]>([]);
  const [existingThumbnailMediaId, setExistingThumbnailMediaId] = useState<string | null>(null);
  const [removedMediaIds, setRemovedMediaIds] = useState<string[]>([]);
  const [photoOrderDirty, setPhotoOrderDirty] = useState(false);
  const thumbnailRef = useRef<HTMLInputElement>(null);
  const photosRef = useRef<HTMLInputElement>(null);

  // --- Step 2: car-specific gate + variations pool ---
  const [isCarSpecific, setIsCarSpecific] = useState(false);
  // Offering-level variation pool. Car-specific: grown from inside car cards (reused across cars).
  // Non-car-specific: managed by the top editor.
  const [variations, setVariations] = useState<VariationDraft[]>([]);

  // Cars (car-specific path)
  const [makes, setMakes] = useState<LookupEntryResponse[]>([]);
  const [carTypes, setCarTypes] = useState<LookupEntryResponse[]>([]);
  const [modelsByMake, setModelsByMake] = useState<Record<string, LookupEntryResponse[]>>({});
  const [cars, setCars] = useState<CarRow[]>([]);
  const [showAddCar, setShowAddCar] = useState(false);

  // Service-level pricing (non-car-specific path)
  const [serviceEnabled, setServiceEnabled] = useState<number[][]>([]);
  const [servicePriceByCombo, setServicePriceByCombo] = useState<Record<string, { price: string; durationOverrideMinutes: string; status?: VariantStatus; rejectionReason?: string | null }>>({});
  const [flatPrice, setFlatPrice] = useState('');
  const [flatDurationDays, setFlatDurationDays] = useState('0');
  const [flatDurationHours, setFlatDurationHours] = useState('4');

  const [error, setError] = useState<string | null>(null);
  // Spec 081a: per-field errors (client checks + the server's 400 `errors` map), keyed like the server.
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  // Ticket #996: the save lost a race with another write (409 CONCURRENT_UPDATE). The partner's
  // input stays on screen; the reload action is theirs to take.
  const [conflict, setConflict] = useState(false);
  // #995: a 422 PRICE_BELOW_OFFER_DISCOUNT, shown on the price field(s) under the offer's minimum.
  const [priceGuard, setPriceGuard] = useState<PriceGuard | null>(null);
  // True when no price field held a price under the minimum at save time (e.g. an older draft was
  // submitted): the message then goes in the banner, since no field would carry it.
  const [priceGuardBanner, setPriceGuardBanner] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [toast, setToast] = useState(false);

  useEffect(() => {
    // Flat list so children carry parentId — enables the parent + subcategory dropdowns.
    api.getCategoriesFlat().then((data) => {
      setCategories(data);
      setLoadingCategories(false);
    }).catch(() => setLoadingCategories(false));
  }, []);

  // Load lookups whenever we are on the car-specific path.
  useEffect(() => {
    if (step === 2 && isCarSpecific && makes.length === 0) {
      api.fetchLookupEntries('CAR_MAKES', undefined, token).then(setMakes).catch(() => setMakes([]));
      api.fetchLookupEntries('CAR_TYPES', undefined, token).then(setCarTypes).catch(() => setCarTypes([]));
    }
  }, [step, isCarSpecific, makes.length, token]);

  // --- Edit mode: preload offering into wizard state ---
  useEffect(() => {
    if (!editOfferingId) return;
    let cancelled = false;
    (async () => {
      try {
        const off = await api.getMyOfferingV2(editOfferingId, tokenRef.current);
        if (cancelled) return;
        hydrateFromOffering(off);
        // The offering response exposes bare URLs; the media list is the only place mediaId
        // lives, and without it a saved photo can be shown but never removed.
        try {
          const media = await listServiceMedia(editOfferingId, tokenRef.current);
          if (cancelled) return;
          const thumb = media.find((m) => m.mediaType === 'SERVICE_THUMBNAIL');
          setExistingThumbnailMediaId(thumb?.mediaId || null);
          if (thumb) setExistingThumbnailUrl(thumb.downloadUrl);
          // sortOrder is the partner's chosen gallery order — the list arrives sorted, and
          // sorting again keeps the tiles honest regardless. Dropping it here (as this did)
          // made the grid show an order the customer would never see.
          setExistingPhotos(
            media
              .filter((m) => m.mediaType === 'SERVICE_PHOTO')
              .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
              .map((m) => ({ mediaId: m.mediaId, downloadUrl: m.downloadUrl }))
          );
        } catch {
          // Keep the URL-only fallback from hydrateFromOffering.
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load service');
      } finally {
        if (!cancelled) setLoadingOffering(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editOfferingId]);

  const hydrateFromOffering = (off: OfferingV2Response) => {
    setName(off.name || '');
    setCategoryId(off.categoryId || '');
    setSubcategoryId(off.subcategoryId || '');
    setPage(servicePageDraftFrom(off));
    setLoadedDurations({ flat: off.flatDurationMinutes ?? null, legacy: off.durationMinutes ?? null });
    setStatus(off.status === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE');
    setServerStatus(off.status ?? null);
    // Display-only fallback; loadExistingMedia() replaces these with id-bearing rows.
    setExistingThumbnailUrl(off.thumbnailUrl || null);
    setExistingPhotos((off.photoUrls || []).map((url) => ({ mediaId: '', downloadUrl: url })));
    const carSpecific = !!off.isCarSpecific;
    setIsCarSpecific(carSpecific);

    const pool: VariationDraft[] = [...off.variations]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((v) => ({
        id: v.id,
        name: v.name,
        nameAr: v.nameAr ?? undefined,
        selectionMode: v.selectionMode ?? 'SINGLE',
        description: v.description ?? '',
        descriptionAr: v.descriptionAr ?? '',
        moreDetails: v.moreDetails ?? '',
        moreDetailsAr: v.moreDetailsAr ?? '',
        values: [...v.values]
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((vv) => ({
            id: vv.id, label: vv.label, labelAr: vv.labelAr ?? undefined,
            description: vv.description || '', descriptionAr: vv.descriptionAr || '',
          })),
      }));
    setVariations(pool);

    // Map value id → [poolIdx, valueIdx]
    const valueIndex = new Map<string, [number, number]>();
    pool.forEach((v, vi) => v.values.forEach((vv, vvi) => { if (vv.id) valueIndex.set(vv.id, [vi, vvi]); }));

    if (carSpecific) {
      const carRows: CarRow[] = [...off.supportedCars]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((sc) => {
          const carVariants = off.variants.filter((vt) => vt.supportedCarId === sc.id);
          // Derive this car's variation refs from its variants.
          const enabledByPool = new Map<number, Set<number>>();
          const usedOrder: number[] = [];
          for (const vt of carVariants) {
            for (const id of vt.variationValueIds) {
              const pos = valueIndex.get(id);
              if (!pos) continue;
              const [pIdx, vIdx] = pos;
              if (!enabledByPool.has(pIdx)) { enabledByPool.set(pIdx, new Set()); usedOrder.push(pIdx); }
              enabledByPool.get(pIdx)!.add(vIdx);
            }
          }
          usedOrder.sort((a, b) => a - b);
          const variationRefs: VariationRef[] = usedOrder.map((pIdx) => ({
            poolIdx: pIdx,
            enabled: Array.from(enabledByPool.get(pIdx)!).sort((a, b) => a - b),
          }));
          // Price map keyed by combo (value indices in variationRefs order).
          const priceByCombo: Record<string, { price: string; durationOverrideMinutes: string; status?: VariantStatus; rejectionReason?: string | null }> = {};
          for (const vt of carVariants) {
            const perPool = new Map<number, number>();
            for (const id of vt.variationValueIds) {
              const pos = valueIndex.get(id);
              if (pos) perPool.set(pos[0], pos[1]);
            }
            const combo = variationRefs.map((r) => perPool.get(r.poolIdx) ?? 0);
            const key = variationRefs.length === 0 ? '' : comboKey(combo);
            priceByCombo[key] = {
              price: String(vt.price),
              durationOverrideMinutes: vt.durationOverrideMinutes != null ? String(vt.durationOverrideMinutes) : '',
              status: vt.status,
              rejectionReason: vt.rejectionReason,
            };
          }
          return {
            localId: nextLocalId(),
            serverCarId: sc.id,
            carMakeLookupId: sc.carMakeLookupId,
            carModelLookupId: sc.carModelLookupId || '',
            carType: sc.carType || '',
            carMakeName: sc.carMakeName || '',
            carModelName: sc.carModelName || '',
            variationRefs,
            priceByCombo,
            expanded: false,
            selected: true,
          };
        });
      setCars(carRows);
      [...new Set(carRows.map((c) => c.carMakeLookupId))].forEach((mk) => {
        api.fetchLookupEntries('CAR_MODELS', mk, tokenRef.current)
          .then((m) => setModelsByMake((prev) => ({ ...prev, [mk]: m }))).catch(() => undefined);
      });
    } else {
      // service-level (non-car-specific)
      const serviceVariants = off.variants.filter((vt) => vt.supportedCarId == null);
      if (pool.length > 0) {
        const enabled: Set<number>[] = pool.map(() => new Set<number>());
        const priceByCombo: Record<string, { price: string; durationOverrideMinutes: string; status?: VariantStatus; rejectionReason?: string | null }> = {};
        for (const vt of serviceVariants) {
          const indices = pool.map(() => -1);
          for (const id of vt.variationValueIds) {
            const pos = valueIndex.get(id);
            if (pos) indices[pos[0]] = pos[1];
          }
          pool.forEach((_, vi) => { if (indices[vi] >= 0) enabled[vi].add(indices[vi]); });
          priceByCombo[comboKey(indices)] = {
            price: String(vt.price),
            durationOverrideMinutes: vt.durationOverrideMinutes != null ? String(vt.durationOverrideMinutes) : '',
            status: vt.status,
            rejectionReason: vt.rejectionReason,
          };
        }
        setServiceEnabled(enabled.map((s, vi) => {
          const arr = Array.from(s).sort((a, b) => a - b);
          return arr.length === 0 ? pool[vi].values.map((_, i) => i) : arr;
        }));
        setServicePriceByCombo(priceByCombo);
      }
      if (off.flatPrice != null) {
        setFlatPrice(String(off.flatPrice));
        const mins = off.flatDurationMinutes ?? 240;
        setFlatDurationDays(String(Math.floor(mins / 1440)));
        setFlatDurationHours(String(Math.floor((mins % 1440) / 60)));
      }
    }
  };

  const parentCategories = useMemo(() => categories.filter((c) => !c.parentId), [categories]);
  const subcategories = useMemo(
    () => (categoryId ? categories.filter((c) => c.parentId === categoryId) : []),
    [categories, categoryId],
  );

  const handleThumbnailSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const err = validateFile(file, 'SERVICE_THUMBNAIL');
    if (err) { setError(err); return; }
    setThumbnail(file);
    setThumbnailPreview(URL.createObjectURL(file));
  };

  const handlePhotosSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const remaining = 19 - additionalPhotos.length - existingPhotos.length;
    const slice = files.slice(0, Math.max(0, remaining));
    for (const f of slice) {
      const err = validateFile(f, 'SERVICE_PHOTO');
      if (err) { setError(err); return; }
    }
    setAdditionalPhotos([...additionalPhotos, ...slice]);
    setAdditionalPreviews([...additionalPreviews, ...slice.map((f) => URL.createObjectURL(f))]);
  };

  const removePhoto = (idx: number) => {
    setAdditionalPhotos(additionalPhotos.filter((_, i) => i !== idx));
    setAdditionalPreviews(additionalPreviews.filter((_, i) => i !== idx));
  };

  // Reordering only applies to already-saved photos: a newly picked file has no media id yet,
  // so it is appended on save and can be moved on the next edit.
  const moveExistingPhoto = (idx: number, delta: number) => {
    const to = idx + delta;
    if (to < 0 || to >= existingPhotos.length) return;
    const next = [...existingPhotos];
    [next[idx], next[to]] = [next[to], next[idx]];
    setExistingPhotos(next);
    setPhotoOrderDirty(true);
  };

  // Saved media is dropped from the grid now and deleted server-side on save, so Cancel
  // still leaves the offering untouched.
  const queueMediaRemoval = (mediaId: string) => {
    if (mediaId) setRemovedMediaIds((prev) => (prev.includes(mediaId) ? prev : [...prev, mediaId]));
  };

  const removeExistingPhoto = (mediaId: string, idx: number) => {
    queueMediaRemoval(mediaId);
    setExistingPhotos(existingPhotos.filter((_, i) => i !== idx));
  };

  const clearThumbnail = () => {
    if (existingThumbnailUrl && existingThumbnailMediaId) queueMediaRemoval(existingThumbnailMediaId);
    setThumbnail(null);
    setThumbnailPreview(null);
    setExistingThumbnailUrl(null);
  };

  const ensureModelsLoaded = useCallback((makeId: string) => {
    if (!makeId || modelsByMake[makeId]) return;
    api.fetchLookupEntries('CAR_MODELS', makeId, token)
      .then((m) => setModelsByMake((prev) => ({ ...prev, [makeId]: m })))
      .catch(() => setModelsByMake((prev) => ({ ...prev, [makeId]: [] })));
  }, [modelsByMake, token]);

  // ── Non-car-specific pool editor (top of step 2 when toggle OFF) ──

  const addServiceVariation = () => {
    if (variations.length >= MAX_VARIATIONS) return;
    const newVar: VariationDraft = { name: '', values: [{ label: '', description: '' }, { label: '', description: '' }] };
    setVariations((prev) => [...prev, newVar]);
    setServiceEnabled((prev) => [...prev, newVar.values.map((_, i) => i)]);
    setServicePriceByCombo({});
  };
  const removeServiceVariation = (idx: number) => {
    setVariations((prev) => prev.filter((_, i) => i !== idx));
    setServiceEnabled((prev) => prev.filter((_, i) => i !== idx));
    setServicePriceByCombo({});
  };
  const addServiceValue = (vi: number) => {
    const newIdx = variations[vi]?.values.length ?? 0;
    // Guard here too: without it the enabled-set below would gain an out-of-range index
    // while the setVariations updater silently no-ops at the cap.
    if (newIdx >= MAX_VALUES_PER_VARIATION) return;
    setVariations((prev) => {
      const next = [...prev];
      if (next[vi].values.length >= MAX_VALUES_PER_VARIATION) return prev;
      next[vi] = { ...next[vi], values: [...next[vi].values, { label: '', description: '' }] };
      return next;
    });
    setServiceEnabled((prev) => prev.map((set, i) => (i === vi ? [...set, newIdx] : set)));
    // Value indices are append-only, so every existing combo key keeps its meaning: existing
    // rows keep their prices and only the new value's combos start empty (#908 item 1).
  };
  const removeServiceValue = (vi: number, vvi: number) => {
    if ((variations[vi]?.values.length ?? 0) <= 2) return;
    setVariations((prev) => prev.map((v, i) => {
      if (i !== vi) return v;
      if (v.values.length <= 2) return v;
      return { ...v, values: v.values.filter((_, j) => j !== vvi) };
    }));
    setServiceEnabled((prev) => prev.map((set, i) =>
      i === vi ? set.filter((x) => x !== vvi).map((x) => (x > vvi ? x - 1 : x)) : set,
    ));
    setServicePriceByCombo((prev) => dropValueFromCombos(prev, vi, vvi));
  };
  const updateServiceVariationName = (idx: number, vName: string) =>
    setVariations((prev) => prev.map((v, i) => i === idx ? { ...v, name: vName } : v));
  const updateServiceValueLabel = (vi: number, vvi: number, label: string) =>
    setVariations((prev) => prev.map((v, i) => {
      if (i !== vi) return v;
      const values = [...v.values];
      values[vvi] = { ...values[vvi], label };
      return { ...v, values };
    }));
  const toggleServiceValue = (vi: number, valueIdx: number) => {
    setServiceEnabled((prev) => {
      const sets = [...prev];
      const cur = sets[vi] || [];
      const next = cur.includes(valueIdx) ? cur.filter((x) => x !== valueIdx) : [...cur, valueIdx].sort((a, b) => a - b);
      if (next.length === 0) return prev;
      sets[vi] = next;
      return sets;
    });
  };
  const updateServicePrice = (key: string, patch: Partial<{ price: string; durationOverrideMinutes: string }>) => {
    setServicePriceByCombo((prev) => {
      const cur = prev[key] || { price: '', durationOverrideMinutes: '' };
      return { ...prev, [key]: { ...cur, ...patch } };
    });
  };
  useEffect(() => {
    if (isCarSpecific) return;
    setServiceEnabled((prev) => {
      if (prev.length === variations.length) return prev;
      return variations.map((v, i) => prev[i] && prev[i].length > 0 ? prev[i] : v.values.map((_, j) => j));
    });
  }, [isCarSpecific, variations]);

  // ── Car editing ──

  const buildEmptyCar = (makeId: string, modelId: string, carType: string): CarRow => ({
    localId: nextLocalId(),
    carMakeLookupId: makeId,
    carModelLookupId: modelId,
    carType,
    carMakeName: makes.find((m) => m.id === makeId)?.name,
    carModelName: (modelsByMake[makeId] || []).find((m) => m.id === modelId)?.name,
    variationRefs: [],                 // by default no variation → single price
    priceByCombo: {},
    expanded: true,
    selected: true,
  });

  const addCarFromDrawer = (makeId: string, modelId: string, carType: string) => {
    // A supported car is valid when EITHER make OR body type is selected.
    // Three shapes: make-only, type-only, make+model (+ optional type).
    const hasMake = !!makeId;
    const hasType = !!(carType && carType.trim());
    if (!hasMake && !hasType) { setError('Pick a make or a body type'); return; }
    if (modelId && !hasMake) { setError('Pick a make before choosing a model'); return; }
    // Same identity (make + model + type) is a distinct car — dedup on the full tuple.
    if (cars.some((c) => carIdentityKey(c.carMakeLookupId, c.carModelLookupId, c.carType) === carIdentityKey(makeId, modelId, carType))) {
      setError('This car is already added.');
      return;
    }
    setCars((prev) => [...prev.map((c) => ({ ...c, expanded: false })), buildEmptyCar(makeId, modelId, carType)]);
    setShowAddCar(false);
    setError(null);
  };

  const removeCar = (localId: string) => setCars((prev) => prev.filter((c) => c.localId !== localId));
  const toggleCarExpand = (localId: string) =>
    setCars((prev) => prev.map((c) => c.localId === localId ? { ...c, expanded: !c.expanded } : c));
  const toggleCarSelect = (localId: string) =>
    setCars((prev) => prev.map((c) => c.localId === localId ? { ...c, selected: !c.selected } : c));

  const patchCar = (localId: string, fn: (c: CarRow) => CarRow) =>
    setCars((prev) => prev.map((c) => (c.localId === localId ? fn(c) : c)));

  // Distinct variations actually used by some car (ignores orphaned pool entries left by removals).
  const liveVariationCount = () => {
    const s = new Set<number>();
    cars.forEach((c) => c.variationRefs.forEach((r) => s.add(r.poolIdx)));
    return s.size;
  };

  // Add a brand-new (empty) variation to this car — it joins the pool and can be reused on other cars.
  const addNewVariationToCar = (localId: string) => {
    if (liveVariationCount() >= MAX_VARIATIONS) return;
    const newPoolIdx = variations.length;
    const newVar: VariationDraft = { name: '', values: [{ label: '', description: '' }, { label: '', description: '' }] };
    setVariations((prev) => [...prev, newVar]);
    patchCar(localId, (c) => ({
      ...c,
      variationRefs: [...c.variationRefs, { poolIdx: newPoolIdx, enabled: [0, 1] }],
      priceByCombo: {},
    }));
  };

  // Reuse a variation defined earlier (on another car).
  const addExistingVariationToCar = (localId: string, poolIdx: number) => {
    patchCar(localId, (c) => {
      if (c.variationRefs.some((r) => r.poolIdx === poolIdx)) return c;
      const enabled = (variations[poolIdx]?.values || []).map((_, i) => i);
      return { ...c, variationRefs: [...c.variationRefs, { poolIdx, enabled }], priceByCombo: {} };
    });
  };

  const removeVariationFromCar = (localId: string, refIdx: number) => {
    patchCar(localId, (c) => ({
      ...c,
      variationRefs: c.variationRefs.filter((_, i) => i !== refIdx),
      priceByCombo: {},
    }));
  };

  // Per-car: switch a value on/off for one of the car's variations (>=1 must stay on).
  const toggleValueForRef = (localId: string, refIdx: number, valueIdx: number) => {
    patchCar(localId, (c) => {
      const refs = [...c.variationRefs];
      const ref = refs[refIdx];
      if (!ref) return c;
      const next = ref.enabled.includes(valueIdx)
        ? ref.enabled.filter((x) => x !== valueIdx)
        : [...ref.enabled, valueIdx].sort((a, b) => a - b);
      if (next.length === 0) return c;
      refs[refIdx] = { ...ref, enabled: next };
      return { ...c, variationRefs: refs };
    });
  };

  const updateCarPrice = (localId: string, key: string, patch: Partial<{ price: string; durationOverrideMinutes: string }>) => {
    patchCar(localId, (c) => {
      const cur = c.priceByCombo[key] || { price: '', durationOverrideMinutes: '' };
      return { ...c, priceByCombo: { ...c.priceByCombo, [key]: { ...cur, ...patch } } };
    });
  };

  // ── Pool-variation editing (affects every car referencing it) ──

  // #908 item 3: one variation per service may be multi-select (the pricing rule sums one
  // variant per picked value with every other variation fixed; two MULTI variations have no rule).
  const setVariationSelectionMode = (poolIdx: number, multi: boolean) =>
    setVariations((prev) => prev.map((v, i) => (i === poolIdx ? { ...v, selectionMode: multi ? 'MULTI' : 'SINGLE' } : v)));
  const renderSelectionModeToggle = (poolIdx: number) => {
    const v = variations[poolIdx];
    if (!v) return null;
    const isMulti = v.selectionMode === 'MULTI';
    const otherMulti = variations.some((o, i) => i !== poolIdx && o.selectionMode === 'MULTI');
    return (
      <label className="v2-selection-mode" title={otherMulti && !isMulti ? 'Only one variation per service can be multi-select' : undefined}>
        <input type="checkbox" checked={isMulti} disabled={otherMulti && !isMulti}
          onChange={(e) => setVariationSelectionMode(poolIdx, e.target.checked)} />
        Customers can pick several values (multi-select)
      </label>
    );
  };

  const editPoolVariationName = (poolIdx: number, vName: string) =>
    setVariations((prev) => prev.map((v, i) => (i === poolIdx ? { ...v, name: vName } : v)));

  const editPoolValueLabel = (poolIdx: number, vvi: number, label: string) =>
    setVariations((prev) => prev.map((v, i) => {
      if (i !== poolIdx) return v;
      const values = [...v.values];
      values[vvi] = { ...values[vvi], label };
      return { ...v, values };
    }));

  // Add a value to a (shared) variation, but enable it ONLY for the car you're editing.
  // Other cars using the same variation keep it unselected by default (and their prices intact).
  const addPoolValue = (localId: string, poolIdx: number) => {
    const newIdx = variations[poolIdx]?.values.length ?? 0;
    if (newIdx >= MAX_VALUES_PER_VARIATION) return;
    setVariations((prev) => prev.map((v, i) =>
      i === poolIdx ? { ...v, values: [...v.values, { label: '', description: '' }] } : v));
    // Appending a value never invalidates existing combo keys (indices are append-only),
    // so the car keeps its already-entered prices — only the new value's combos start empty.
    patchCar(localId, (c) => ({
      ...c,
      variationRefs: c.variationRefs.map((r) => r.poolIdx === poolIdx ? { ...r, enabled: [...r.enabled, newIdx] } : r),
    }));
  };

  const removePoolValue = (poolIdx: number, vvi: number) => {
    if ((variations[poolIdx]?.values.length ?? 0) <= 2) return;
    setVariations((prev) => prev.map((v, i) =>
      i === poolIdx ? { ...v, values: v.values.filter((_, j) => j !== vvi) } : v));
    // #413 per-car isolation: editing a shared variation from one car must not destroy
    // the other cars' pricing state. Instead of wiping each affected car's priceByCombo,
    // remap its keys — drop only the rows that referenced the removed value and shift
    // higher value indices down. Cars that never enabled the value keep every price.
    setCars((prev) => prev.map((c) => {
      const refIdx = c.variationRefs.findIndex((r) => r.poolIdx === poolIdx);
      if (refIdx === -1) return c;
      const variationRefs = c.variationRefs.map((r, i) => {
        if (i !== refIdx) return r;
        const enabled = r.enabled.filter((x) => x !== vvi).map((x) => (x > vvi ? x - 1 : x));
        return { ...r, enabled: enabled.length ? enabled : [0] };
      });
      const priceByCombo: CarRow['priceByCombo'] = {};
      for (const [key, cell] of Object.entries(c.priceByCombo)) {
        if (key === '') { priceByCombo[key] = cell; continue; }
        const combo = key.split('_').map(Number);
        const val = combo[refIdx];
        if (val === vvi) continue;              // row priced the removed value — gone with it
        if (val > vvi) combo[refIdx] = val - 1; // shift past the removal
        priceByCombo[comboKey(combo)] = cell;
      }
      return { ...c, variationRefs, priceByCombo };
    }));
  };

  // ── Validation ──

  const step1Valid = !!name.trim() && !!categoryId && !!page.description.trim();

  // Pool entries that are actually used (car-specific = referenced by a car; otherwise the full pool).
  const usedPoolIdxs = useMemo(() => {
    if (!isCarSpecific) return variations.map((_, i) => i);
    const set = new Set<number>();
    cars.forEach((c) => c.variationRefs.forEach((r) => set.add(r.poolIdx)));
    return Array.from(set).sort((a, b) => a - b);
  }, [isCarSpecific, cars, variations]);

  const variationsValid = usedPoolIdxs.every((i) => {
    const v = variations[i];
    return v && v.name.trim() && v.values.every((vv) => vv.label.trim());
  });

  const advance = () => {
    if (step === 1) {
      const pageErrors = validateServicePage(page);
      setFieldErrors(pageErrors);
      if (!step1Valid) { setError('Please complete all required fields in step 1.'); return; }
      if (Object.keys(pageErrors).length > 0) { setError('Some fields need attention — see the highlighted fields.'); return; }
      setError(null);
      setStep(2);
    }
  };
  const goBack = () => { setError(null); if (step === 2) setStep(1); };

  const carHasAnyPrice = (c: CarRow) => {
    if (c.variationRefs.length === 0) {
      return !!c.priceByCombo[''] && parseFloat(c.priceByCombo[''].price || '') > 0;
    }
    for (const combo of carCombos(c.variationRefs)) {
      const p = c.priceByCombo[comboKey(combo)]?.price;
      if (p && parseFloat(p) > 0) return true;
    }
    return false;
  };

  // Prune to used pool variations + remap old pool index → payload index.
  const effectivePool = (): { list: VariationDraft[]; map: Map<number, number> } => {
    const map = new Map<number, number>();
    const list: VariationDraft[] = [];
    usedPoolIdxs.forEach((oldIdx) => { map.set(oldIdx, list.length); list.push(variations[oldIdx]); });
    return { list, map };
  };

  const variationPayloadFrom = (list: VariationDraft[]): VariationPayload[] => list.map((v, i) => ({
    name: v.name.trim(),
    sortOrder: i,
    selectionMode: v.selectionMode ?? 'SINGLE',
    description: trimOrUndefined(v.description),
    descriptionAr: trimOrUndefined(v.descriptionAr),
    moreDetails: trimOrUndefined(v.moreDetails),
    moreDetailsAr: trimOrUndefined(v.moreDetailsAr),
    values: v.values.map((vv, j) => ({
      label: vv.label.trim(), description: vv.description.trim() || undefined,
      descriptionAr: trimOrUndefined(vv.descriptionAr), sortOrder: j,
    })),
  }));

  // Replace semantics: option texts are always sent ("" clears; the server stores null). nameAr /
  // labelAr are not edited here but are sent back as loaded, so a save no longer wipes them.
  const replaceVariationsPayloadFrom = (list: VariationDraft[]): ReplaceVariationsRequest => ({
    variations: list.map((v, i) => ({
      id: v.id ?? null,
      name: v.name.trim(),
      nameAr: v.nameAr || undefined,
      sortOrder: i,
      selectionMode: v.selectionMode ?? 'SINGLE',
      description: (v.description ?? '').trim(),
      descriptionAr: (v.descriptionAr ?? '').trim(),
      moreDetails: (v.moreDetails ?? '').trim(),
      moreDetailsAr: (v.moreDetailsAr ?? '').trim(),
      values: v.values.map((vv, j) => ({
        id: vv.id ?? null, label: vv.label.trim(), labelAr: vv.labelAr || undefined,
        description: vv.description.trim() || undefined, descriptionAr: trimOrUndefined(vv.descriptionAr), sortOrder: j,
      })),
    })),
  });

  // ── Spec 081a: option / value text editing (pool-level, shared by every car using the option) ──
  const patchVariationTexts = (poolIdx: number, patch: Partial<Pick<VariationDraft, 'description' | 'descriptionAr' | 'moreDetails' | 'moreDetailsAr'>>) =>
    setVariations((prev) => prev.map((v, i) => (i === poolIdx ? { ...v, ...patch } : v)));
  const patchValueDescription = (poolIdx: number, vvi: number, patch: { description?: string; descriptionAr?: string }) =>
    setVariations((prev) => prev.map((v, i) => {
      if (i !== poolIdx) return v;
      const values = [...v.values];
      values[vvi] = { ...values[vvi], ...patch };
      return { ...v, values };
    }));
  const renderVariationTexts = (poolIdx: number) => {
    const v = variations[poolIdx];
    if (!v) return null;
    return (
      <VariationTextsEditor
        optionName={v.name}
        value={{ description: v.description ?? '', descriptionAr: v.descriptionAr ?? '', moreDetails: v.moreDetails ?? '', moreDetailsAr: v.moreDetailsAr ?? '' }}
        onChange={(patch) => patchVariationTexts(poolIdx, patch)}
        errorPrefix={`variations[${poolIdx}]`}
        errors={fieldErrors}
      />
    );
  };

  // Build per-car variant rows against a server offering's variations + the old→new pool index map.
  const buildCarRows = (c: CarRow, serverVariations: OfferingV2Response['variations'], idxMap: Map<number, number>): UpsertVariantRow[] => {
    const rows: UpsertVariantRow[] = [];
    if (c.variationRefs.length === 0) {
      const cell = c.priceByCombo[''];
      if (cell && parseFloat(cell.price || '') > 0) {
        rows.push({ variationValueIds: [], price: parseFloat(cell.price), durationOverrideMinutes: cell.durationOverrideMinutes ? parseInt(cell.durationOverrideMinutes, 10) : null });
      }
      return rows;
    }
    for (const combo of carCombos(c.variationRefs)) {
      const cell = c.priceByCombo[comboKey(combo)];
      if (!cell || !cell.price || parseFloat(cell.price) <= 0) continue;
      const valueIds = combo.map((valIdx, i) => {
        const newIdx = idxMap.get(c.variationRefs[i].poolIdx);
        return newIdx != null ? serverVariations[newIdx]?.values[valIdx]?.id : undefined;
      }).filter(Boolean) as string[];
      if (valueIds.length !== c.variationRefs.length) continue;
      rows.push({ variationValueIds: valueIds, price: parseFloat(cell.price), durationOverrideMinutes: cell.durationOverrideMinutes ? parseInt(cell.durationOverrideMinutes, 10) : null });
    }
    return rows;
  };

  const buildServiceRows = (serverVariations: OfferingV2Response['variations']): UpsertVariantRow[] => {
    const rows: UpsertVariantRow[] = [];
    for (const combo of (variations.length === 0 ? [[]] : cartesian(serviceEnabled))) {
      const cell = servicePriceByCombo[comboKey(combo)];
      if (!cell || !cell.price || parseFloat(cell.price) <= 0) continue;
      const valueIds = combo.map((valIdx, vi) => serverVariations[vi]?.values[valIdx]?.id).filter(Boolean) as string[];
      if (valueIds.length !== variations.length) continue;
      rows.push({ variationValueIds: valueIds, price: parseFloat(cell.price), durationOverrideMinutes: cell.durationOverrideMinutes ? parseInt(cell.durationOverrideMinutes, 10) : null });
    }
    return rows;
  };

  const validateForSubmit = (): string | null => {
    if (!step1Valid) return 'Please complete all required fields in step 1.';
    if (Object.keys(validateServicePage(page)).length > 0) return 'Some fields need attention — see the highlighted fields.';
    if (!variationsValid) return 'Each variation needs a name and every value needs a label.';
    const duplicate = duplicateVariationError(effectivePool().list);
    if (duplicate) return duplicate;
    if (isCarSpecific) {
      if (cars.length === 0) return 'Add at least one car.';
      const targets = cars.filter((c) => (isEdit ? true : c.selected));
      if (targets.length === 0) return 'Select at least one car to submit.';
      if (targets.filter(carHasAnyPrice).length === 0) return 'Add a price for at least one car.';
    } else if (variations.length > 0) {
      if (Object.values(servicePriceByCombo).every((c) => !c.price || parseFloat(c.price) <= 0)) {
        return 'Add a price for at least one variation combination.';
      }
    } else {
      const fp = parseFloat(flatPrice);
      if (!fp || fp <= 0) return 'Flat price must be greater than zero.';
    }
    return null;
  };

  const uploadMedia = async (offeringId: string) => {
    // Deletions first: a queued thumbnail removal must retire before a replacement lands,
    // and a photo the partner removed should not survive a later upload failure.
    for (const mediaId of removedMediaIds) {
      setUploadProgress('Removing images…');
      await deleteServiceMedia(offeringId, mediaId, token);
    }
    if (removedMediaIds.length > 0) setRemovedMediaIds([]);
    if (thumbnail) { setUploadProgress('Uploading thumbnail…'); await uploadServiceMedia(offeringId, thumbnail, 'SERVICE_THUMBNAIL', token); }
    const uploadedIds: string[] = [];
    for (let i = 0; i < additionalPhotos.length; i++) {
      setUploadProgress(`Uploading photo ${i + 1} of ${additionalPhotos.length}…`);
      const saved = await uploadServiceMedia(offeringId, additionalPhotos[i], 'SERVICE_PHOTO', token);
      if (saved?.mediaId) uploadedIds.push(saved.mediaId);
    }

    // Persist the gallery order once, after the set of photos has settled. Uploads append, so
    // this is only needed when the partner actually moved a tile; new photos keep their
    // upload order at the end either way.
    if (photoOrderDirty) {
      const orderedIds = [
        ...existingPhotos.map((p) => p.mediaId).filter(Boolean),
        ...uploadedIds,
      ];
      if (orderedIds.length > 1) {
        setUploadProgress('Saving photo order…');
        await api.reorderServiceMedia(offeringId, orderedIds, token);
      }
      setPhotoOrderDirty(false);
    }
  };

  const totalFlatMinutes = () => (parseInt(flatDurationDays || '0', 10) * 1440) + (parseInt(flatDurationHours || '0', 10) * 60);

  // ── Create ──

  const handleCreate = async (submitForReview: boolean) => {
    const totalMinutes = totalFlatMinutes();
    const flat = !isCarSpecific && variations.length === 0;
    const { list: pool, map: idxMap } = effectivePool();

    // make/model/type may each be null — the wizard supports type-only, make-only, or full combos.
    const supportedCars: SupportedCarPayload[] = isCarSpecific ? cars.map((c, i) => ({
      carMakeLookupId: c.carMakeLookupId || null,
      carType: c.carType || null,
      carModelLookupId: c.carModelLookupId || null,
      sortOrder: i,
    })) : [];

    const initialReq: CreateOfferingV2Request = {
      name: name.trim(),
      categoryId,
      subcategoryId: subcategoryId || null,
      ...servicePageCreateFields(page),
      status,
      isCarSpecific,
      flatPrice: flat ? parseFloat(flatPrice) : null,
      flatDurationMinutes: flat ? (totalMinutes || 240) : null,
      supportedCars,
      variations: pool.length ? variationPayloadFrom(pool) : [],
      variants: [],
    };

    setUploadProgress('Creating service…');
    const created = await api.createOfferingV2(initialReq, token);

    if (isCarSpecific) {
      // One bulk request, one transaction — same rule as the edit path (pitfall #42:
      // never save cars in a per-car request loop).
      const bulkCars: BulkUpsertCarVariantsRequest['cars'] = [];
      for (let ci = 0; ci < cars.length; ci++) {
        const serverCar = created.supportedCars[ci];
        if (!serverCar) continue;
        const rows = buildCarRows(cars[ci], created.variations, idxMap);
        if (rows.length === 0) continue;
        bulkCars.push({ carId: serverCar.id, variants: rows });
      }
      if (bulkCars.length > 0) {
        setUploadProgress(`Saving prices for ${bulkCars.length} car${bulkCars.length === 1 ? '' : 's'}…`);
        await api.upsertCarVariantsBulkV2(created.id, { cars: bulkCars }, token);
      }
    } else if (variations.length > 0) {
      const rows = buildServiceRows(created.variations);
      if (rows.length > 0) { setUploadProgress('Saving variation prices…'); await api.upsertServiceVariantsV2(created.id, { variants: rows }, token); }
    }

    if (submitForReview) {
      const carIds = isCarSpecific
        ? cars.map((c, ci) => ({ c, srv: created.supportedCars[ci] })).filter(({ c }) => c.selected).map(({ srv }) => srv?.id).filter(Boolean) as string[]
        : [];
      setUploadProgress('Submitting for approval…');
      await api.submitOfferingDraftsV2(created.id, { carIds: carIds.length > 0 ? carIds : undefined }, token);
    }

    await uploadMedia(created.id);
    return created.id;
  };

  // ── Edit (full save) ──

  const handleEdit = async (submitForReview: boolean) => {
    const id = editOfferingId!;
    const totalMinutes = totalFlatMinutes();
    const flat = !isCarSpecific && variations.length === 0;
    const { list: pool, map: idxMap } = effectivePool();

    setUploadProgress('Saving basics…');
    const basics: UpdateOfferingV2Request = {
      name: name.trim(), categoryId, subcategoryId: subcategoryId || null,
      ...servicePageUpdateFields(page),
      ...(statusToggleable ? { status } : {}), isCarSpecific,
      flatPrice: flat ? parseFloat(flatPrice) : null,
      flatDurationMinutes: flat ? (totalMinutes || 240) : null,
    };
    // Media first: small, and usually the thing the partner actually came to change.
    // Sequencing it last meant a thumbnail edit only landed after all the pricing work
    // succeeded — so it never landed at all once the save outlived the token.
    setUploadProgress('Saving image…');
    await uploadMedia(id);

    await api.updateOfferingV2(id, basics, token);

    setUploadProgress('Saving variations…');
    await api.replaceVariationsV2(id, replaceVariationsPayloadFrom(pool), token);

    if (isCarSpecific) {
      const fresh0 = await api.getMyOfferingV2(id, token);
      const localServerIds = new Set(cars.map((c) => c.serverCarId).filter(Boolean) as string[]);
      for (const sc of fresh0.supportedCars) {
        if (!localServerIds.has(sc.id)) { setUploadProgress('Removing cars…'); await api.removeCarFromOfferingV2(id, sc.id, token); }
      }
      for (const c of cars) {
        if (!c.serverCarId) {
          const label = c.carMakeName || c.carType || 'car';
          setUploadProgress(`Adding ${label}${c.carModelName ? ' ' + c.carModelName : ''}…`);
          await api.addCarToOfferingV2(id, {
            carMakeLookupId: c.carMakeLookupId || null,
            carType: c.carType || null,
            carModelLookupId: c.carModelLookupId || null,
          }, token);
        }
      }
      const fresh = await api.getMyOfferingV2(id, token);
      const freshVariations = [...fresh.variations].sort((a, b) => a.sortOrder - b.sortOrder);
      // Match newly-added cars by full identity (make|model|type); existing cars keep their serverCarId.
      const carIdByIdentity = new Map<string, string>();
      fresh.supportedCars.forEach((sc) => carIdByIdentity.set(carIdentityKey(sc.carMakeLookupId || '', sc.carModelLookupId || '', sc.carType || ''), sc.id));

      const currentByCar = new Map<string, typeof fresh.variants>();
      fresh.variants.forEach((v) => {
        if (!v.supportedCarId) return;
        const list = currentByCar.get(v.supportedCarId) || [];
        list.push(v);
        currentByCar.set(v.supportedCarId, list);
      });

      const submittedCarIds: string[] = [];
      const changedCars: BulkUpsertCarVariantsRequest['cars'] = [];
      for (const c of cars) {
        const serverCarId = c.serverCarId || carIdByIdentity.get(carIdentityKey(c.carMakeLookupId, c.carModelLookupId, c.carType));
        if (!serverCarId) continue;
        // Selection drives what gets submitted for review, independently of whether the
        // prices changed — an unchanged car the partner ticked must still be submitted.
        if (c.selected) submittedCarIds.push(serverCarId);
        const rows = buildCarRows(c, freshVariations, idxMap);
        if (sameVariantSet(rows, currentByCar.get(serverCarId) || [])) continue;
        changedCars.push({ carId: serverCarId, variants: rows });
      }

      // One request, one transaction — it cannot half-apply the way the old per-car loop did.
      if (changedCars.length > 0) {
        setUploadProgress(`Saving prices for ${changedCars.length} car${changedCars.length === 1 ? '' : 's'}…`);
        await api.upsertCarVariantsBulkV2(id, { cars: changedCars }, token);
      }
      if (submitForReview) { setUploadProgress('Submitting for approval…'); await api.submitOfferingDraftsV2(id, { carIds: submittedCarIds.length > 0 ? submittedCarIds : undefined }, token); }
    } else if (variations.length > 0) {
      const fresh = await api.getMyOfferingV2(id, token);
      const freshVariations = [...fresh.variations].sort((a, b) => a.sortOrder - b.sortOrder);
      const rows = buildServiceRows(freshVariations);
      setUploadProgress('Saving variation prices…');
      await api.upsertServiceVariantsV2(id, { variants: rows }, token);
      if (submitForReview) { setUploadProgress('Submitting for approval…'); await api.submitOfferingDraftsV2(id, {}, token); }
    }

    return id;
  };

  const handleSubmit = async (submitForReview: boolean) => {
    const validationError = validateForSubmit();
    if (validationError) {
      const pageErrors = validateServicePage(page);
      setFieldErrors(pageErrors);
      if (Object.keys(pageErrors).length > 0) setStep(1);
      setError(validationError);
      return;
    }
    setError(null);
    setFieldErrors({});
    setConflict(false);
    setPriceGuard(null);
    setPriceGuardBanner(false);
    setSubmitting(true);
    try {
      if (isEdit) await handleEdit(submitForReview); else await handleCreate(submitForReview);
      setUploadProgress(null);
      setToast(true);
      setTimeout(() => { onSuccess(); onClose(); }, 1400);
    } catch (err) {
      const guard = priceGuardOf(err);
      const serverFieldErrors = fieldErrorsOf(err);
      if (isConcurrentUpdateError(err)) {
        setConflict(true);
      } else if (serverFieldErrors) {
        // Spec 081a AC-8: show each 400 field error on its input; every input keeps its value.
        setFieldErrors(remapVariationErrorKeys(serverFieldErrors, usedPoolIdxs));
        if (hasStep1Error(serverFieldErrors)) setStep(1);
        setError(allErrorsInline(serverFieldErrors)
          ? 'Some fields need attention — see the highlighted fields.'
          : (err instanceof Error ? err.message : 'Some fields need attention.'));
      } else if (guard) {
        // Shown on the price fields, not as a banner; open every car holding a price under the minimum.
        setPriceGuard(guard);
        setPriceGuardBanner(!(
          belowGuard(flatPrice, guard)
          || Object.values(servicePriceByCombo).some((cell) => belowGuard(cell.price, guard))
          || cars.some((c) => Object.values(c.priceByCombo).some((cell) => belowGuard(cell.price, guard)))));
        setCars((prev) => prev.map((c) => (Object.values(c.priceByCombo).some((cell) => belowGuard(cell.price, guard))
          ? { ...c, expanded: true } : c)));
      } else {
        setError(err instanceof Error ? err.message : `Failed to ${isEdit ? 'update' : 'create'} service`);
      }
      setUploadProgress(null);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCount = cars.filter((c) => c.selected).length;
  const primaryLabel = isEdit
    ? 'Submit for Approval'
    : (isCarSpecific ? `Submit Selected (${selectedCount})` : 'Post Service');

  // Spec 081a: duration is edited in step 2 and only shown (read-only) in step 1 and the preview,
  // using the backend rule (design §2b) via durationRange(). In a variant mode the wizard sends
  // flatDurationMinutes: null, which an edit leaves unchanged, so the loaded value is the row fallback.
  const previewDuration = (() => {
    const flatMode = !isCarSpecific && variations.length === 0;
    if (flatMode) return durationRange([], totalFlatMinutes() || null, loadedDurations.legacy);
    const flat = isEdit ? loadedDurations.flat : null;
    const rows: (number | null)[] = [];
    const collect = (cell?: { price: string; durationOverrideMinutes: string }) => {
      if (!cell || !(parseFloat(cell.price || '') > 0)) return;
      const m = parseInt(cell.durationOverrideMinutes || '', 10);
      rows.push(m > 0 ? m : null);
    };
    if (isCarSpecific) {
      cars.forEach((c) => carCombos(c.variationRefs).forEach((combo) => collect(c.priceByCombo[comboKey(combo)])));
    } else {
      cartesian(serviceEnabled).forEach((combo) => collect(servicePriceByCombo[comboKey(combo)]));
    }
    return durationRange(rows, flat, loadedDurations.legacy);
  })();

  // ── Render helpers ──

  const renderCarVariation = (c: CarRow, ref: VariationRef, refIdx: number) => {
    const v = variations[ref.poolIdx];
    if (!v) return null;
    return (
      <div key={refIdx} className="v2-variation-card">
        <div className="v2-variation-header">
          <input type="text" className="v2-variation-name" placeholder="Variation name (e.g. Cylinders)"
            value={v.name} onChange={(e) => editPoolVariationName(ref.poolIdx, e.target.value)} />
          <button onClick={() => removeVariationFromCar(c.localId, refIdx)} className="v2-row-remove" title="Remove this variation from the car">×</button>
        </div>
        {renderSelectionModeToggle(ref.poolIdx)}
        {renderVariationTexts(ref.poolIdx)}
        <div className="v2-values-list">
          {v.values.map((vv, vvi) => {
            const on = ref.enabled.includes(vvi);
            return (
              <ValueDescriptionEditor key={vvi} valueLabel={vv.label} description={vv.description} descriptionAr={vv.descriptionAr ?? ''}
                onChange={(patch) => patchValueDescription(ref.poolIdx, vvi, patch)}
                errorPrefix={`variations[${ref.poolIdx}].values[${vvi}]`} errors={fieldErrors}>
                <button type="button" className={`v2-chip v2-chip--toggle${on ? '' : ' v2-chip--off'}`}
                  onClick={() => toggleValueForRef(c.localId, refIdx, vvi)} title={on ? 'Applies to this car' : 'Not offered for this car'}>
                  {on ? '✓' : '✕'}
                </button>
                <input type="text" placeholder={`Value ${vvi + 1} (e.g. 4)`} value={vv.label}
                  onChange={(e) => editPoolValueLabel(ref.poolIdx, vvi, e.target.value)} />
                <button onClick={() => removePoolValue(ref.poolIdx, vvi)} disabled={v.values.length <= 2} className="v2-value-remove" title="Delete this value">×</button>
              </ValueDescriptionEditor>
            );
          })}
        </div>
        <button onClick={() => addPoolValue(c.localId, ref.poolIdx)} disabled={v.values.length >= MAX_VALUES_PER_VARIATION} className="v2-add-btn v2-add-btn-small">+ Add value</button>
      </div>
    );
  };

  const renderPriceGrid = (
    combos: number[][],
    labelFor: (combo: number[]) => string,
    priceMap: Record<string, { price: string; durationOverrideMinutes: string; status?: VariantStatus; rejectionReason?: string | null }>,
    onPrice: (key: string, patch: Partial<{ price: string; durationOverrideMinutes: string }>) => void,
  ) => (
    <div className="v2-price-grid">
      {priceGuard && priceGuard.field !== 'flatPrice'
        && combos.some((combo) => belowGuard(priceMap[comboKey(combo)]?.price ?? '', priceGuard)) && (
        <div className="v2-price-guard" role="alert">{priceGuardMessage(priceGuard, offersT, lang)}</div>
      )}
      <div className="v2-price-grid__head">
        <div className="v2-price-grid__cell--combo">Combo</div>
        <div className="v2-price-grid__cell--price">Price (AED) *</div>
        <div className="v2-price-grid__cell--duration">Duration override (days + hours · optional)</div>
        <div className="v2-price-grid__cell--status">Status</div>
      </div>
      {combos.map((combo) => {
        const key = comboKey(combo);
        const cell = priceMap[key] || { price: '', durationOverrideMinutes: '' };
        const showReason = cell.status === 'REJECTED' || cell.status === 'NEEDS_CHANGES';
        return (
          <div key={key || 'flat'}>
            <div className="v2-price-grid__row">
              <div className="v2-price-grid__cell--combo">{labelFor(combo)}</div>
              <div className="v2-price-grid__cell--price">
                <input type="number" min="0" step="0.01" placeholder="0.00" value={cell.price}
                  className={belowGuard(cell.price, priceGuard) ? 'v2-price-input--guard' : undefined}
                  aria-invalid={belowGuard(cell.price, priceGuard) || undefined}
                  onChange={(e) => onPrice(key, { price: e.target.value })} />
              </div>
              <div className="v2-price-grid__cell--duration">
                <DurationOverrideInput minutes={cell.durationOverrideMinutes}
                  onChange={(m) => onPrice(key, { durationOverrideMinutes: m })} />
              </div>
              <div className="v2-price-grid__cell--status">
                {renderVariantStatusBadge(cell.status)}
              </div>
            </div>
            {showReason && cell.rejectionReason && (
              <div className={`v2-variant-reason v2-variant-reason--${cell.status === 'REJECTED' ? 'rejected' : 'needs-changes'}`}>
                <strong>{cell.status === 'REJECTED' ? 'Rejected:' : 'Changes requested:'}</strong> {cell.rejectionReason}
                <span className="v2-variant-reason__hint">— edit and submit again</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderVariantStatusBadge = (status?: VariantStatus) => {
    if (!status) return null;
    const label =
      status === 'ACTIVE' ? 'Approved'
      : status === 'REJECTED' ? 'Rejected'
      : status === 'NEEDS_CHANGES' ? 'Changes requested'
      : status === 'PENDING_APPROVAL' ? 'Awaiting review'
      : status === 'DRAFT' ? 'Draft'
      : status;
    const mod =
      status === 'ACTIVE' ? 'approved'
      : status === 'REJECTED' ? 'rejected'
      : status === 'NEEDS_CHANGES' ? 'changes'
      : status === 'PENDING_APPROVAL' ? 'pending'
      : 'draft';
    return <span className={`v2-variant-status v2-variant-status--${mod}`}>{label}</span>;
  };

  // Per-status variant counts for one car ("3 approved · 1 rejected · 2 pending") so a
  // car with a single rejected variant no longer reads as wholly rejected. Empty in
  // create mode (no server statuses yet).
  const carStatusBreakdown = (c: CarRow): { mod: string; label: string; count: number }[] => {
    const counts = new Map<VariantStatus, number>();
    Object.values(c.priceByCombo).forEach((cell) => {
      if (cell.status) counts.set(cell.status, (counts.get(cell.status) || 0) + 1);
    });
    const order: { status: VariantStatus; mod: string; label: string }[] = [
      { status: 'ACTIVE', mod: 'approved', label: 'approved' },
      { status: 'REJECTED', mod: 'rejected', label: 'rejected' },
      { status: 'NEEDS_CHANGES', mod: 'changes', label: 'changes requested' },
      { status: 'PENDING_APPROVAL', mod: 'pending', label: 'pending' },
      { status: 'DRAFT', mod: 'draft', label: 'draft' },
    ];
    return order
      .filter((o) => counts.has(o.status))
      .map((o) => ({ mod: o.mod, label: o.label, count: counts.get(o.status)! }));
  };

  const renderCarStatusSummary = (c: CarRow) => {
    const breakdown = carStatusBreakdown(c);
    if (breakdown.length === 0) {
      return <span className="status-badge status-badge--draft">{c.serverCarId ? 'EDIT' : 'DRAFT'}</span>;
    }
    return (
      <span style={{ whiteSpace: 'nowrap' }}>
        {breakdown.map((b, i) => (
          <span key={b.mod}>
            {i > 0 && <span style={{ color: '#9ca3af' }}> · </span>}
            <span className={`v2-variant-status v2-variant-status--${b.mod}`}>{b.count} {b.label}</span>
          </span>
        ))}
      </span>
    );
  };

  const renderServiceVariationsEditor = () => (
    <div className="v2-section">
      <div className="partner-modal-section-title">Variations (optional)</div>
      <p className="v2-hint">Add a variation (e.g. Cylinders → 4 · 6 · 8) and set a price per combination, or leave empty for a single flat price.</p>
      {variations.length === 0 && <div className="v2-empty">No variations yet. Add one, or leave empty for a single price.</div>}
      {variations.map((v, vi) => (
        <div key={vi} className="v2-variation-card">
          <div className="v2-variation-header">
            <input type="text" className="v2-variation-name" placeholder="Variation name (e.g. Cylinders)"
              value={v.name} onChange={(e) => updateServiceVariationName(vi, e.target.value)} />
            <button onClick={() => removeServiceVariation(vi)} className="v2-row-remove" title="Remove variation">×</button>
          </div>
          {renderSelectionModeToggle(vi)}
          {renderVariationTexts(vi)}
          <div className="v2-values-list">
            {v.values.map((vv, vvi) => (
              <ValueDescriptionEditor key={vvi} valueLabel={vv.label} description={vv.description} descriptionAr={vv.descriptionAr ?? ''}
                onChange={(patch) => patchValueDescription(vi, vvi, patch)}
                errorPrefix={`variations[${vi}].values[${vvi}]`} errors={fieldErrors}>
                <input type="text" placeholder={`Value ${vvi + 1} (e.g. 4)`} value={vv.label}
                  onChange={(e) => updateServiceValueLabel(vi, vvi, e.target.value)} />
                <button onClick={() => removeServiceValue(vi, vvi)} disabled={v.values.length <= 2} className="v2-value-remove" title="Remove value">×</button>
              </ValueDescriptionEditor>
            ))}
          </div>
          <button onClick={() => addServiceValue(vi)} disabled={v.values.length >= MAX_VALUES_PER_VARIATION} className="v2-add-btn v2-add-btn-small">+ Add value</button>
        </div>
      ))}
      <button onClick={addServiceVariation} disabled={variations.length >= MAX_VARIATIONS} className="v2-add-btn">+ Add variation</button>
    </div>
  );

  return (
    <>
      <div className="partner-modal-overlay">
        <div className="partner-modal" style={{ maxWidth: step === 1 ? '1180px' : '960px', maxHeight: '94vh', overflow: 'auto' }}>
          <div className="partner-modal-header">
            <h2>{isEdit ? 'Edit Service' : 'Add New Service'}</h2>
            <button className="partner-modal-close" onClick={onClose}>&times;</button>
          </div>

          <div className="v2-wizard-stepper">
            <span className={`v2-step ${step >= 1 ? 'active' : ''}`}>1. Basics</span>
            <span className="v2-step-sep">›</span>
            <span className={`v2-step ${step >= 2 ? 'active' : ''}`}>2. Cars &amp; Pricing</span>
          </div>

          <div className="partner-modal-body">
            {error && <div className="error-message" style={{ marginBottom: '1rem' }}>{error}</div>}
            {conflict && (
              <div className="error-message" role="alert" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
                <span>{CONCURRENT_UPDATE_MESSAGE}</span>
                <button
                  type="button"
                  className="partner-modal-btn-cancel"
                  onClick={() => {
                    // Edit: re-open on the latest server copy. Create: the drafts already exist
                    // server-side, so close and let the list refresh show them.
                    if (isEdit) onReload(); else { onSuccess(); onClose(); }
                  }}
                >
                  Reload
                </button>
              </div>
            )}
            {priceGuard && priceGuardBanner && (
              <div className="error-message" role="alert" style={{ marginBottom: '1rem' }}>{priceGuardMessage(priceGuard, offersT, lang)}</div>
            )}
            {loadingOffering && <div className="v2-empty">Loading service…</div>}

            {!loadingOffering && step === 1 && (
              <div className="svc-step1">
              <div className="svc-step1__form">
                <div className="partner-modal-section-title">Service basics</div>

                <div className="partner-modal-form-row-3">
                  <div className="partner-modal-form-group">
                    <label>Service Name *</label>
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Periodic Maintenance" />
                  </div>
                  <div className="partner-modal-form-group">
                    <label>Category *</label>
                    <select value={categoryId} onChange={(e) => { setCategoryId(e.target.value); setSubcategoryId(''); }} disabled={loadingCategories}>
                      <option value="">{loadingCategories ? 'Loading…' : 'Select category'}</option>
                      {parentCategories.map((cat) => (<option key={cat.id} value={cat.id}>{cat.name}</option>))}
                    </select>
                  </div>
                  <div className="partner-modal-form-group">
                    <label>Subcategory</label>
                    <select value={subcategoryId} onChange={(e) => setSubcategoryId(e.target.value)} disabled={!categoryId || subcategories.length === 0}>
                      <option value="">{subcategories.length === 0 ? '— none —' : 'Select subcategory'}</option>
                      {subcategories.map((sub) => (<option key={sub.id} value={sub.id}>{sub.name}</option>))}
                    </select>
                  </div>
                </div>

                <ServicePageSection value={page} onChange={patchPage} errors={fieldErrors} duration={previewDuration} />

                <h4 style={{ margin: '1rem 0 0.5rem', fontSize: '0.9rem' }}>Status</h4>
                {statusToggleable ? (
                  <div className="partner-modal-radio-group">
                    <label className="partner-modal-radio-label">
                      <input type="radio" name="status" checked={status === 'ACTIVE'} onChange={() => setStatus('ACTIVE')} /> Active
                    </label>
                    <label className="partner-modal-radio-label">
                      <input type="radio" name="status" checked={status === 'INACTIVE'} onChange={() => setStatus('INACTIVE')} /> Inactive
                    </label>
                  </div>
                ) : (
                  <p data-testid="offering-status-locked" style={{ margin: 0, fontSize: '0.85rem' }}>
                    {serverStatus === 'REJECTED' ? 'Rejected' : 'Awaiting approval'} — the service goes live once a Garagat admin approves it.
                  </p>
                )}

                <div className="v2-photos-grid">
                  <div>
                    <h4 style={{ margin: '1rem 0 0.5rem', fontSize: '0.9rem' }}>Thumbnail{isEdit ? '' : ' *'}</h4>
                    <input ref={thumbnailRef} type="file" accept="image/jpeg,image/png" style={{ display: 'none' }} onChange={handleThumbnailSelect} />
                    {(thumbnailPreview || existingThumbnailUrl) ? (
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <img src={thumbnailPreview || existingThumbnailUrl || ''} alt="Thumbnail" style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 8, border: '1px solid #ddd' }} />
                        <button onClick={clearThumbnail} className="v2-img-x" aria-label="Remove thumbnail" title="Remove thumbnail">×</button>
                      </div>
                    ) : (
                      <div onClick={() => thumbnailRef.current?.click()} className="v2-dropzone">
                        <div style={{ fontSize: '1.5rem' }}>📷</div>
                        <p style={{ margin: 0, fontSize: '0.85rem' }}>Upload thumbnail</p>
                      </div>
                    )}
                  </div>

                  <div>
                    <h4 style={{ margin: '1rem 0 0.5rem', fontSize: '0.9rem' }}>More photos (up to 19)</h4>
                    <input ref={photosRef} type="file" accept="image/jpeg,image/png" multiple style={{ display: 'none' }} onChange={handlePhotosSelect} />
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {existingPhotos.map((p, idx) => (
                        <div key={p.mediaId || `ex-${idx}`} style={{ position: 'relative' }}>
                          <img src={p.downloadUrl} alt={`Photo ${idx + 1}`} style={{ width: 70, height: 70, objectFit: 'cover', borderRadius: 8, border: '1px solid #ddd', opacity: 0.85 }} />
                          {p.mediaId && (
                            <button onClick={() => removeExistingPhoto(p.mediaId, idx)} className="v2-img-x" aria-label={`Remove photo ${idx + 1}`} title="Remove photo">×</button>
                          )}
                          {p.mediaId && (
                            <div className="v2-photo-tile-order">
                              <button
                                type="button"
                                className="v2-photo-move"
                                disabled={idx === 0}
                                aria-label={`Move photo ${idx + 1} earlier`}
                                title="Move earlier"
                                onClick={() => moveExistingPhoto(idx, -1)}
                              >‹</button>
                              <span className="v2-photo-tile-position">{idx + 1}</span>
                              <button
                                type="button"
                                className="v2-photo-move"
                                disabled={idx === existingPhotos.length - 1}
                                aria-label={`Move photo ${idx + 1} later`}
                                title="Move later"
                                onClick={() => moveExistingPhoto(idx, 1)}
                              >›</button>
                            </div>
                          )}
                        </div>
                      ))}
                      {additionalPreviews.map((p, idx) => (
                        <div key={idx} style={{ position: 'relative' }}>
                          <img src={p} alt={`Photo ${existingPhotos.length + idx + 1}`} style={{ width: 70, height: 70, objectFit: 'cover', borderRadius: 8, border: '1px solid #ddd' }} />
                          <button onClick={() => removePhoto(idx)} className="v2-img-x" aria-label={`Remove photo ${existingPhotos.length + idx + 1}`} title="Remove photo">×</button>
                        </div>
                      ))}
                      {(additionalPhotos.length + existingPhotos.length) < 19 && (
                        <div onClick={() => photosRef.current?.click()} className="v2-dropzone v2-small">
                          <span style={{ fontSize: '1.3rem' }}>📷</span>
                        </div>
                      )}
                    </div>
                    {existingPhotos.length > 1 && (
                      <p className="v2-photos-order-hint">
                        Photo 1 is shown first in the gallery. The thumbnail above is separate and
                        is not part of the gallery.
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <aside className="svc-step1__preview" aria-label="Service page preview">
                <ServicePagePreview
                  name={name}
                  draft={page}
                  thumbnailUrl={thumbnailPreview || existingThumbnailUrl}
                  durationMinutes={previewDuration}
                />
              </aside>
              </div>
            )}

            {!loadingOffering && step === 2 && (
              <>
                <div className="v2-gate-row">
                  <div className="v2-gate-row__icon" aria-hidden>⚙</div>
                  <div className="v2-gate-row__copy">
                    <div className="v2-gate-row__title">Is this service car-specific?</div>
                    <div className="v2-gate-row__sub">Turn on if the price changes depending on the customer's car (make + model).</div>
                  </div>
                  <label className="v2-toggle-switch">
                    <input type="checkbox" checked={isCarSpecific} onChange={(e) => setIsCarSpecific(e.target.checked)} />
                    <span className="v2-toggle-switch__slider" />
                  </label>
                </div>

                {isCarSpecific ? (
                  <div className="v2-section">
                    <div className="v2-cars-header">
                      <div>
                        <div className="partner-modal-section-title">Cars ({cars.length})</div>
                        <p className="v2-hint">Add a car, then inside its card choose to add a variation (or reuse one from another car), or leave it with no variation for a single price.</p>
                      </div>
                      <button className="v2-add-car-btn" onClick={() => setShowAddCar(true)}>+ Add car</button>
                    </div>

                    {cars.length === 0 && (
                      <div className="v2-empty">No cars added yet. Click <strong>+ Add car</strong> to start.</div>
                    )}

                    <div className="v2-car-list">
                      {cars.map((c) => {
                        const reusable = variations
                          .map((v, i) => ({ v, i }))
                          .filter(({ i, v }) => !c.variationRefs.some((r) => r.poolIdx === i) && v.name.trim());
                        const combos = carCombos(c.variationRefs);
                        const labelFor = (combo: number[]) => c.variationRefs.length === 0
                          ? 'Single price'
                          : combo.map((valIdx, i) => variations[c.variationRefs[i].poolIdx]?.values[valIdx]?.label || '—').join(' · ');
                        return (
                          <div key={c.localId} className={`v2-car-card${c.selected ? ' v2-car-card--selected' : ''}${c.expanded ? ' v2-car-card--expanded' : ''}`}>
                            <div className="v2-car-card__header">
                              <label className="v2-car-card__check" onClick={(e) => e.stopPropagation()}>
                                <input type="checkbox" checked={c.selected} onChange={() => toggleCarSelect(c.localId)} />
                              </label>
                              <div className="v2-car-card__name" onClick={() => toggleCarExpand(c.localId)}>
                                <strong>{
                                  (() => {
                                    // Build a readable label from whichever fields are present.
                                    if (c.carMakeName) {
                                      return `${c.carMakeName}${c.carModelName ? ` ${c.carModelName}` : ''}${c.carType ? ` · ${c.carType}` : ''}`;
                                    }
                                    if (c.carType) return `Any ${c.carType}`;
                                    return 'Car';
                                  })()
                                }</strong>
                              </div>
                              {renderCarStatusSummary(c)}
                              <button className="v2-car-card__expand" onClick={() => toggleCarExpand(c.localId)} aria-label="Toggle">{c.expanded ? '▴' : '▾'}</button>
                            </div>

                            {c.expanded && (
                              <div className="v2-car-card__body">
                                {c.variationRefs.length === 0 && (
                                  <div className="v2-empty" style={{ marginBottom: '0.5rem' }}>No variation — this car has a single price. Add a variation below if its price depends on options.</div>
                                )}

                                {c.variationRefs.map((ref, refIdx) => renderCarVariation(c, ref, refIdx))}

                                <div className="v2-car-variation-actions" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', margin: '0.25rem 0 0.75rem' }}>
                                  <button onClick={() => addNewVariationToCar(c.localId)} disabled={liveVariationCount() >= MAX_VARIATIONS} className="v2-add-btn v2-add-btn-small">+ Add new variation</button>
                                  {reusable.length > 0 && (
                                    <select className="v2-reuse-select" value="" onChange={(e) => { if (e.target.value !== '') addExistingVariationToCar(c.localId, parseInt(e.target.value, 10)); }}>
                                      <option value="">Select existing variation…</option>
                                      {reusable.map(({ v, i }) => (<option key={i} value={i}>{v.name}</option>))}
                                    </select>
                                  )}
                                </div>

                                {renderPriceGrid(combos, labelFor, c.priceByCombo, (key, patch) => updateCarPrice(c.localId, key, patch))}

                                <div className="v2-car-card__remove">
                                  <button className="v2-row-remove" onClick={() => removeCar(c.localId)} title="Remove car">🗑 Remove car</button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <>
                    {renderServiceVariationsEditor()}
                    {variations.length > 0 ? (
                      <div className="v2-section">
                        <div className="partner-modal-section-title">Pricing per variation</div>
                        <p className="v2-hint">Set a price for each variation combination.</p>
                        <div className="v2-variations-toggle">
                          {variations.map((v, vi) => (
                            <div key={vi} className="v2-variation-row">
                              <div className="v2-variation-row__label">{v.name || `Variation ${vi + 1}`}</div>
                              <div className="v2-variation-row__chips">
                                {v.values.map((vv, vvi) => {
                                  const on = (serviceEnabled[vi] || []).includes(vvi);
                                  return (
                                    <button key={vvi} className={`v2-chip v2-chip--toggle${on ? '' : ' v2-chip--off'}`}
                                      onClick={() => toggleServiceValue(vi, vvi)}>
                                      {vv.label || `value ${vvi + 1}`}{on ? ' ✓' : ' ✕'}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                        {renderPriceGrid(
                          cartesian(serviceEnabled),
                          (combo) => combo.map((valIdx, i) => variations[i]?.values[valIdx]?.label || '—').join(' · '),
                          servicePriceByCombo, updateServicePrice,
                        )}
                      </div>
                    ) : (
                      <div className="v2-section">
                        <div className="partner-modal-section-title">Single price</div>
                        <p className="v2-hint">This service has one fixed price for all customers, regardless of car.</p>
                        <div className="partner-modal-form-row-3">
                          <div className="partner-modal-form-group">
                            <label>Price (AED) *</label>
                            <input type="number" min="0" step="0.01" value={flatPrice} onChange={(e) => setFlatPrice(e.target.value)} placeholder="0.00"
                              className={belowGuard(flatPrice, priceGuard) ? 'v2-price-input--guard' : undefined}
                              aria-invalid={belowGuard(flatPrice, priceGuard) || undefined} />
                            {priceGuard && belowGuard(flatPrice, priceGuard) && (
                              <div className="v2-price-guard" role="alert">{priceGuardMessage(priceGuard, offersT, lang)}</div>
                            )}
                          </div>
                          <div className="partner-modal-form-group">
                            <label>Duration — days</label>
                            <select value={flatDurationDays} onChange={(e) => setFlatDurationDays(e.target.value)}>
                              {Array.from({ length: 31 }, (_, i) => (<option key={i} value={String(i)}>{i}</option>))}
                            </select>
                          </div>
                          <div className="partner-modal-form-group">
                            <label>Duration — hours</label>
                            <select value={flatDurationHours} onChange={(e) => setFlatDurationHours(e.target.value)}>
                              {Array.from({ length: 24 }, (_, i) => (<option key={i} value={String(i)}>{i}</option>))}
                            </select>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          <div className="partner-modal-footer v2-sticky-actions">
            {uploadProgress && <span style={{ fontSize: '0.85rem', color: '#666', marginRight: 'auto' }}>{uploadProgress}</span>}
            {step > 1 && <button className="partner-modal-btn-cancel" onClick={goBack}>Back</button>}
            <button className="partner-modal-btn-cancel" onClick={onClose}>Cancel</button>
            {step < 2 && <button className="partner-modal-btn-primary" onClick={advance} disabled={loadingOffering}>Next</button>}
            {step === 2 && (
              <>
                {(isEdit || isCarSpecific) && (
                  <button className="partner-modal-btn-cancel" disabled={submitting} onClick={() => handleSubmit(false)}>
                    {submitting ? 'Saving…' : 'Save Draft'}
                  </button>
                )}
                <button className="partner-modal-btn-primary" disabled={submitting} onClick={() => handleSubmit(true)}>
                  {submitting ? (uploadProgress || 'Saving…') : primaryLabel}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {showAddCar && (
        <AddCarDrawer
          makes={makes}
          carTypes={carTypes}
          modelsByMake={modelsByMake}
          ensureModelsLoaded={ensureModelsLoaded}
          existingPairs={cars.map((c) => carIdentityKey(c.carMakeLookupId, c.carModelLookupId, c.carType))}
          onClose={() => setShowAddCar(false)}
          onAdd={addCarFromDrawer}
        />
      )}

      {toast && <div className="partner-toast success">{isEdit ? 'Service updated.' : 'Service created — drafts saved.'}</div>}
    </>
  );
}

interface AddCarDrawerProps {
  makes: LookupEntryResponse[];
  carTypes: LookupEntryResponse[];
  modelsByMake: Record<string, LookupEntryResponse[]>;
  ensureModelsLoaded: (makeId: string) => void;
  existingPairs: string[];
  onClose: () => void;
  onAdd: (makeId: string, modelId: string, carType: string) => void;
}

function AddCarDrawer({ makes, carTypes, modelsByMake, ensureModelsLoaded, existingPairs, onClose, onAdd }: AddCarDrawerProps) {
  const [makeId, setMakeId] = useState('');
  const [modelId, setModelId] = useState('');
  const [carType, setCarType] = useState('');

  const onPickMake = (id: string) => { setMakeId(id); setModelId(''); if (id) ensureModelsLoaded(id); };

  const models = modelsByMake[makeId] || [];
  const typeKey = carType && carType.trim() ? carType.trim() : '';
  const hasMake = !!makeId;
  const hasType = !!typeKey;
  // Valid shapes: type-only, make-only, make+model, make+model+type, make+type.
  const canAdd = hasMake || hasType;
  const dup = canAdd && existingPairs.includes(`${makeId}|${modelId}|${typeKey}`);

  return (
    <div className="v2-add-car-drawer">
      <div className="v2-add-car-drawer__panel">
        <div className="v2-add-car-drawer__header">
          <h3>Add a car</h3>
          <button onClick={onClose} className="partner-modal-close">&times;</button>
        </div>
        <div className="v2-add-car-drawer__body">
          <p className="v2-add-car-drawer__hint">Pick a make, a body type, or both — at least one is required.</p>
          <div className="partner-modal-form-group">
            <label>Make (optional)</label>
            <select value={makeId} onChange={(e) => onPickMake(e.target.value)}>
              <option value="">— Any make —</option>
              {makes.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div className="partner-modal-form-group">
            <label>Model (optional)</label>
            <select value={modelId} disabled={!makeId} onChange={(e) => setModelId(e.target.value)}>
              <option value="">{makeId ? 'All models (make only)' : 'Pick a make first'}</option>
              {models.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div className="partner-modal-form-group">
            <label>Body type (optional)</label>
            <select value={carType} onChange={(e) => setCarType(e.target.value)}>
              <option value="">— Any body type —</option>
              {carTypes.map((t) => <option key={t.id} value={t.code}>{t.name}</option>)}
            </select>
          </div>
          {dup && <div className="error-message">This car is already added.</div>}
          {!canAdd && <div className="v2-add-car-drawer__warn">Pick a make or a body type to continue.</div>}
        </div>
        <div className="v2-add-car-drawer__footer">
          <button className="partner-modal-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="partner-modal-btn-primary" disabled={!canAdd || dup}
            onClick={() => onAdd(makeId, modelId, carType)}>
            Add car
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Ticket #996: "Reload" remounts the body under a new key, so every piece of local state (edits,
 * pending uploads, removed media) is dropped and the service is fetched fresh from the server.
 */
function AddServiceModalV2(props: Props) {
  const [reloadKey, setReloadKey] = useState(0);
  return <AddServiceModalV2Body key={reloadKey} {...props} onReload={() => setReloadKey((k) => k + 1)} />;
}

export default AddServiceModalV2;
